import { Component, computed, input, model, OnInit, output, signal } from '@angular/core';
import { AppCard } from './AppCard';
import { AppFilter } from './AppFilter';
import { AppSuggestion } from './AppSuggestion';
import { Button } from './Button';
import { Wrap } from './Wrap';
import { CategoryService } from "../services/category/category.service";
import { ApiService } from "../services/api/api.service";
import { API, Category } from "../model";
import { logViewApiList } from "../util/analytics.util";
import { ActivatedRoute } from "@angular/router";
import { SharedModule } from "../modules/shared/shared.module";
import { ModalService } from "../modules/shared/modal/modal.service";
import { MissingYoursModalComponent } from "../pages/apps/missing-yours/missing-yours-modal.component";
import { Meta, Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { SUPPORTED_CONTENT_CATEGORIES } from "../pages/apps/apps.component";
import { firstValueFrom } from "rxjs";

@Component({
  selector: 'Apps',
  imports: [SharedModule, AppCard, AppFilter, AppSuggestion, Button, Wrap],
  template: `
    <section
        class="
      pb-15 lg:pb-20 relative my-section
      {{ background() && 'bg-gradient-to-b from-white to-blue-50' }}
      "
    >
      <Wrap [class]="'md:-mt-5'">
        <h2 class="text-xl md:text-3xl mb-4 md:mb-8">
          {{ (title() ? title() : 'APPS_TITLE') | translate }}
        </h2>

        @if (loaded() && showFilter()) {
          <AppFilter
              [categoryId]="categoryId()"
              [categories]="categories()"
              (updateCategory)="updateCategory($event)"
              (updateQuery)="updateQuery($event)"
          ></AppFilter>
        }

        @if (loaded() && apis()) {
          <div
              class="grid grid-cols-2 xs:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5 md:gap-6"
              (window:resize)="setAppsAmount()"
          >
            @for (app of visibleApps(); track app.id) {
              <a
                  class="my-button text-center group transform hover:scale-105 transition-transform"
                  [href]="app.href"
              >
                <AppCard [name]="app.name" [logo]="app.nameNormalized + '.svg'"></AppCard>

                <h3 class="text-base mb-0 md:mb-2 group-hover:text-blue-500 transition-colors">
                  {{ app.name }}
                </h3>
              </a>
            }

            @if (showApps() <= 0 || filteredApps().length < showApps() + 1) {
              <span
                  class="text-center group cursor-pointer"
                  (click)="showMissingYoursModal()"

              >
            <AppSuggestion></AppSuggestion>

            <h3 class="text-base group-hover:text-blue-500 transition-colors mb-0">
              {{ 'APPS_MISSING_TITLE' | translate }}
            </h3>

            <p class="font-mark-heavy font-extrabold text-blue-500 mb-0 md:mb-2 underline decoration-2 underline-offset-4 leading-snug">
              {{ 'APPS_MISSING_CTA' | translate }}
            </p>
          </span>
            }
          </div>
        }

        <app-loading></app-loading>
      </Wrap>

      @if (apis() && !showAll() && showApps() > 0 && filteredApps().length > showApps()) {
        <div
            class="absolute w-full flex justify-center bottom-0 transform translate-y-1/2"

        >
          <Button color="blue" (click)="moreApps()">+ {{ 'APPS_BUTTON_MORE' | translate }}</Button>
        </div>
      }
    </section>
  `
})
export class Apps implements OnInit {
  background = input(true);
  // model(): may be set by a parent, but is also changed by the route and the category filter
  categoryId = model('');
  // filter on a category's internalKey (e.g. 'bookkeeping'), which unlike its id is the same in every environment
  categoryKey = input<string>();
  showFilter = input(true);
  showAll = input(false);
  title = input<string>();
  categoryChanged = output<string>();

  readonly loaded = signal(false);

  readonly apis = signal<API[] | undefined>(undefined);
  readonly categories = signal<Category[]>([]);

  readonly showApps = signal(6);
  readonly query = signal('');

  readonly filteredApps = computed(() => {
    let apps = this.apis() ?? [];

    const query = this.query().toLowerCase();
    if (query) {
      apps = apps.filter(app => app.name.toLowerCase().indexOf(query) !== -1);
    }

    const categoryId = this.categoryId();
    if (categoryId) {
      apps = apps.filter(app => app.categories.indexOf(+categoryId) > -1);
    }

    return apps;
  });

  readonly visibleApps = computed(() => {
    const apps = this.filteredApps();
    const showApps = this.showApps();
    return showApps > 0 ? apps.slice(0, showApps) : apps;
  });

  constructor(private categoryService: CategoryService,
              public route: ActivatedRoute,
              private titleService: Title,
              private metaService: Meta,
              private translate: TranslateService,
              private apiService: ApiService,
              private modalService: ModalService<MissingYoursModalComponent>) {
  }

  async ngOnInit() {
    this.setAppsAmount();
    const integrationsPath = await firstValueFrom(this.translate.get('BREADCRUMBS_INTEGRATIONS_ROUTE'));
    this.apiService.getApis().then(apis => {
      apis.map(api => api.href = "/" + integrationsPath + "/" + Apps.escapeParentheses(api.name) + "/" + api.internalKey);

      // add a few pseudo-apps
      this.injectApiAlphabetically({
        id: 99991,
        name: "ING Checkout",
        nameNormalized: "ingcheckout",
        href: "/" + integrationsPath + "/Pay./Pay?store=ING Checkout",
        categories: [7, 800] // payments (local and demo/prod)
      } as API, apis);

      this.injectApiAlphabetically({
        id: 99992,
        name: "MailBlue",
        nameNormalized: "mailblue",
        href: "/" + integrationsPath + "/ActiveCampaign/ActiveCampaign?store=MailBlue",
        categories: [4, 400] // emailmarketing (local and demo/prod)
      } as API, apis);

      this.apis.set(apis);
    });

    const category = this.route.snapshot.params["category"];
    if (category) {
      await this.loadCategories();
      this.loaded.set(true);
      const selectedCategory = this.categories().filter(cat => cat.name.toLowerCase() === category)[0];
      if (selectedCategory) {
        this.categoryId.set("" + selectedCategory.id);

        const pageTitle = integrationsPath + " > " + selectedCategory.name;
        this.titleService.setTitle(pageTitle);
        this.metaService.updateTag({
          name: "description",
          content: pageTitle
        });
      }
    } else if (this.categoryKey()) {
      await this.loadCategories();
      const selectedCategory = this.categories().find(cat => cat.internalKey === this.categoryKey());
      if (selectedCategory) {
        this.categoryId.set("" + selectedCategory.id);
      }
      this.loaded.set(true);
    } else {
      // noinspection ES6MissingAwait (no need to await this)
      this.loadCategories();
      this.loaded.set(true);
    }
  }

  moreApps() {
    this.showApps.set(-1);
  }

  setAppsAmount() {
    if (this.showAll()) {
      this.showApps.set(9999);
    } else if (this.showApps() > 0) {
      if (window.innerWidth >= 1280) {
        this.showApps.set(12);
      } else if (window.innerWidth >= 1024) {
        this.showApps.set(10);
      } else if (window.innerWidth >= 768) {
        this.showApps.set(8);
      } else {
        this.showApps.set(6);
      }
    }
  }

  updateCategory(categoryId: string) {
    this.categoryId.set(categoryId);
    if (categoryId) {
      const category = this.categories().filter(cat => cat.id === +categoryId)[0];
      this.categoryChanged.emit(SUPPORTED_CONTENT_CATEGORIES[category.name.toLowerCase()]);
      logViewApiList(category.internalKey, category.name, this.filteredApps());
    }
  }

  updateQuery(value: string) {
    this.query.set(value);
  }

  async showMissingYoursModal() {
    const modalService: ModalService<MissingYoursModalComponent> = this.modalService.create(MissingYoursModalComponent);
    await modalService.open();
  }

  private injectApiAlphabetically(api: API, apis: Array<API>) {
    let insertIndex = 0;
    const SKIP_TOP_12_APPS = 12; // we have kindof a fixed list at the top
    for (let i = SKIP_TOP_12_APPS; i < apis.length; i++) {
      insertIndex = i;
      if (apis[i].name.toLowerCase() > api.name.toLowerCase()) {
        break;
      }
    }
    apis.splice(insertIndex, 0, api);
  }

  private static escapeParentheses(what: string): string {
    return what
    .replace("(", '%28')
    .replace(")", '%29');
  }

  private async loadCategories() {
    this.categories.set(await this.categoryService.getCategories());
  }
}
